import { useState, useImperativeHandle, forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import { k8sResourceService } from '@/services/k8sResourceService'

interface UpdateFlowConfirmModalProps {
  canDirectUpdate: boolean // platform owner or project owner
  canRequestUpdate: boolean // platform owner or project owner or project admin
  onSuccess?: () => void
}

export interface UpdateFlowConfirmModalRef {
  open: (flowData: { name: string; namespace: string; manifest: any; configName: string }) => void
  close: () => void
}

const UpdateFlowConfirmModal = forwardRef<UpdateFlowConfirmModalRef, UpdateFlowConfirmModalProps>(
  ({ canDirectUpdate, canRequestUpdate, onSuccess }, ref) => {
    const { t } = useTranslation('pipeline-studio')
    const [isOpen, setIsOpen] = useState(false)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [success, setSuccess] = useState(false)
    const [flowData, setFlowData] = useState<{ name: string; namespace: string; manifest: any; configName: string } | null>(null)
    const [actionType, setActionType] = useState<'direct' | 'request'>('direct')

    useImperativeHandle(ref, () => ({
      open: (data: { name: string; namespace: string; manifest: any; configName: string }) => {
        setFlowData(data)
        // If can direct update, default to direct; otherwise request
        setActionType(canDirectUpdate ? 'direct' : 'request')
        setIsOpen(true)
        setError('')
        setSuccess(false)
      },
      close: () => {
        setIsOpen(false)
        setFlowData(null)
        setActionType('direct')
        setError('')
        setSuccess(false)
      },
    }))

    const handleSubmit = async () => {
      if (!flowData || loading) return

      try {
        setLoading(true)
        setError('')

        if (actionType === 'direct') {
          // Direct update flow
          await k8sResourceService.updateResource(flowData.manifest)
        } else {
          // Submit request
          await k8sResourceService.requestUpdateResource(flowData.manifest)
        }

        // Show success message instead of closing immediately
        setSuccess(true)
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'errors.operationFailed'
        if (errorMessage.startsWith('errors.')) {
          setError(t(errorMessage, { ns: 'common', defaultValue: errorMessage }))
        } else {
          setError(errorMessage)
        }
      } finally {
        setLoading(false)
      }
    }

    const handleClose = () => {
      const wasSuccess = success
      setIsOpen(false)
      setFlowData(null)
      setActionType('direct')
      setError('')
      setSuccess(false)

      // Only call onSuccess if the operation was successful
      if (wasSuccess) {
        onSuccess?.()
      }
    }

    if (!isOpen || !flowData) return null

    return (
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-white/30 backdrop-blur-md">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="p-6 border-b border-gray-200 sticky top-0 bg-white">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-primary">
                  {t('updateModal.title', 'Confirm Flow Update')}
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  {t('updateModal.subtitle', 'Please review the configuration and confirm')}
                </p>
              </div>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                disabled={loading}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 space-y-4">
            {/* Success Message */}
            {success && (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-green-600 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-green-900 mb-1">
                      {actionType === 'direct'
                        ? t('updateModal.successDirect', 'Flow Updated!')
                        : t('updateModal.successRequest', 'Update Request Submitted!')}
                    </p>
                    <p className="text-xs text-green-700">
                      {actionType === 'direct'
                        ? t('updateModal.successDirectDesc', 'Flow has been successfully updated.')
                        : t('updateModal.successRequestDesc', 'Request is awaiting approval.')}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && !success && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                <svg className="w-5 h-5 text-red-600 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {/* Info Message */}
            {!success && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-blue-900 mb-1">
                      {actionType === 'direct'
                        ? t('updateModal.confirmDirect', 'Confirm Direct Update?')
                        : t('updateModal.confirmRequest', 'Confirm Submission of Update Request?')}
                    </p>
                    <p className="text-xs text-blue-700">
                      {actionType === 'direct'
                        ? t('updateModal.warningDirect', 'The flow configuration will be updated immediately.')
                        : t('updateModal.warningRequest', 'The update request will be submitted to the project owner for approval.')}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Flow Summary */}
            {!success && (
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm font-semibold text-gray-900 mb-3">
                  {t('updateModal.updateSummary', 'Update Configuration Summary:')}
                </p>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-700 min-w-[100px]">{t('flow.name')}:</span>
                    <span className="text-sm text-gray-900 font-mono">{flowData.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-700 min-w-[100px]">{t('flow.namespace')}:</span>
                    <span className="text-sm text-gray-900 font-mono">{flowData.namespace}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-700 min-w-[100px]">{t('updateModal.configType', 'Configuration Type')}:</span>
                    <span className="text-sm text-gray-900">{flowData.configName}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Action Type Selection (Only if can do both) */}
            {!success && canDirectUpdate && canRequestUpdate && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('confirmModal.actionType')}
                </label>
                <div className="flex gap-3">
                  <label className="flex-1 cursor-pointer">
                    <input
                      type="radio"
                      name="actionType"
                      value="direct"
                      checked={actionType === 'direct'}
                      onChange={(e) => setActionType(e.target.value as 'direct' | 'request')}
                      disabled={loading}
                      className="sr-only"
                    />
                    <div className={`px-4 py-3 border-2 rounded-lg transition-all ${
                      actionType === 'direct'
                        ? 'border-primary bg-primary/5'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}>
                      <span className="text-sm font-medium text-gray-900 block">
                        {t('updateModal.directUpdate', 'Direct Update')}
                      </span>
                      <p className="text-xs text-gray-600 mt-1">
                        {t('updateModal.directUpdateDesc', 'Update flow immediately')}
                      </p>
                    </div>
                  </label>
                  <label className="flex-1 cursor-pointer">
                    <input
                      type="radio"
                      name="actionType"
                      value="request"
                      checked={actionType === 'request'}
                      onChange={(e) => setActionType(e.target.value as 'direct' | 'request')}
                      disabled={loading}
                      className="sr-only"
                    />
                    <div className={`px-4 py-3 border-2 rounded-lg transition-all ${
                      actionType === 'request'
                        ? 'border-primary bg-primary/5'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}>
                      <span className="text-sm font-medium text-gray-900 block">
                        {t('confirmModal.submitRequest')}
                      </span>
                      <p className="text-xs text-gray-600 mt-1">
                        {t('updateModal.submitRequestDesc', 'Create approval request')}
                      </p>
                    </div>
                  </label>
                </div>
              </div>
            )}

            {/* Admin/Non-owner Info Notice */}
            {!success && !canDirectUpdate && canRequestUpdate && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <div className="flex-1">
                    <p className="text-sm text-blue-700">
                      {t('updateModal.requestNotice', 'You can submit a flow update request, which requires project owner approval.')}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="p-6 border-t border-gray-200 flex items-center justify-end gap-3 sticky bottom-0 bg-white">
            {success ? (
              <button
                type="button"
                onClick={handleClose}
                className="px-5 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
              >
                {t('confirmModal.close')}
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={loading}
                >
                  {t('confirmModal.cancel')}
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  disabled={loading}
                >
                  {loading && (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  )}
                  <span>
                    {loading
                      ? t('confirmModal.processing')
                      : actionType === 'direct'
                        ? t('updateModal.confirmUpdate', 'Confirm Update')
                        : t('confirmModal.submitRequest')}
                  </span>
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    )
  }
)

UpdateFlowConfirmModal.displayName = 'UpdateFlowConfirmModal'

export default UpdateFlowConfirmModal
